import type { OrganizationSummary } from '../../organizations/lib/types'
import { buildDemoApprovalOverview } from './demoData'
import type { ApprovalEvent, ApprovalItem, ApprovalOverview } from './types'

const inMemoryApprovalStore = new Map<string, ApprovalItem[]>()
const inMemoryApprovalEventsStore = new Map<string, ApprovalEvent[]>()

function getOrganizationKey(organization: OrganizationSummary | null) {
  return organization?.id ?? 'global'
}

function write(organization: OrganizationSummary | null, items: ApprovalItem[]) {
  inMemoryApprovalStore.set(getOrganizationKey(organization), items)
}

function writeEvents(organization: OrganizationSummary | null, events: ApprovalEvent[]) {
  inMemoryApprovalEventsStore.set(getOrganizationKey(organization), events)
}

function readEvents(organization: OrganizationSummary | null): ApprovalEvent[] {
  const events = inMemoryApprovalEventsStore.get(getOrganizationKey(organization))
  if (!events) {
    return buildInitialEvents(readDemoApprovalOverview(organization).items)
  }

  return events
}

function buildInitialEvents(items: ApprovalItem[]): ApprovalEvent[] {
  const timestamp = new Date().toISOString()

  return items.map((item) => ({
    id: `event-initial-${item.id}`,
    approvalId: item.id,
    fromStatus: null,
    toStatus: item.status,
    comment: item.comment,
    actorLabel: 'System',
    createdAt: timestamp,
  }))
}

function appendEvent(
  organization: OrganizationSummary | null,
  approvalId: string,
  fromStatus: ApprovalItem['status'] | null,
  toStatus: ApprovalItem['status'],
  comment: string | null,
  actorLabel: string,
) {
  const events = readEvents(organization)

  writeEvents(organization, [
    {
      id: `event-${approvalId}-${Date.now()}`,
      approvalId,
      fromStatus,
      toStatus,
      comment,
      actorLabel,
      createdAt: new Date().toISOString(),
    },
    ...events,
  ])
}

function buildOverview(items: ApprovalItem[]): ApprovalOverview {
  const now = Date.now()
  return {
    pendingCount: items.filter((item) => item.status === 'pending').length,
    approvedCount: items.filter((item) => item.status === 'approved').length,
    changesRequestedCount: items.filter((item) => item.status === 'changes_requested').length,
    overdueCount: items.filter((item) => item.status === 'pending' && item.dueAt && new Date(item.dueAt).getTime() < now)
      .length,
    items,
  }
}

function nextDueAt() {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  date.setHours(12, 0, 0, 0)
  return date.toISOString()
}

export function readDemoApprovalOverview(organization: OrganizationSummary | null): ApprovalOverview {
  const items = inMemoryApprovalStore.get(getOrganizationKey(organization))
  if (!items) {
    return buildDemoApprovalOverview(organization)
  }

  return buildOverview(
    items.map((item) => ({
      ...item,
      requestedFromUserId: item.requestedFromUserId ?? null,
      assigneeLabel: item.assigneeLabel ?? 'Nicht zugewiesen',
    })),
  )
}

export function seedDemoApprovalOverview(organization: OrganizationSummary | null) {
  const overview = readDemoApprovalOverview(organization)
  write(organization, overview.items)
  if (readEvents(organization).length === 0) {
    writeEvents(organization, buildInitialEvents(overview.items))
  }
  return overview
}

export function readDemoApprovalEvents(organization: OrganizationSummary | null, approvalId: string): ApprovalEvent[] {
  const events = readEvents(organization)

  if (events.length === 0) {
    const seeded = buildInitialEvents(readDemoApprovalOverview(organization).items)
    writeEvents(organization, seeded)
    return seeded.filter((event) => event.approvalId === approvalId)
  }

  return events.filter((event) => event.approvalId === approvalId)
}

export function readAllDemoApprovalEvents(organization: OrganizationSummary | null): ApprovalEvent[] {
  return readEvents(organization)
}

export function updateDemoApprovalItem(
  organization: OrganizationSummary | null,
  approvalId: string,
  status: ApprovalItem['status'],
  comment?: string,
) {
  const overview = readDemoApprovalOverview(organization)
  const currentItem = overview.items.find((item) => item.id === approvalId)
  if (!currentItem) {
    return
  }

  if (currentItem.status === status && (comment ?? currentItem.comment) === currentItem.comment) {
    return
  }

  const items = overview.items.map((item) =>
    item.id === approvalId
      ? {
          ...item,
          status,
          comment: comment ?? item.comment,
          contentStatus:
            status === 'approved'
              ? 'approved'
              : status === 'changes_requested' || status === 'skipped'
                ? 'draft'
                : item.contentStatus,
          dueAt: status === 'pending' ? item.dueAt ?? nextDueAt() : item.dueAt,
        }
      : item,
  )

  write(organization, items)
  appendEvent(organization, approvalId, currentItem.status, status, comment ?? currentItem.comment ?? null, 'Team')
}

export function syncDemoApprovalByContentItem(input: {
  organization: OrganizationSummary | null
  contentItemId: string
  title: string
  channel: string
  contentType: ApprovalItem['contentType']
  contentStatus: ApprovalItem['contentStatus']
  scheduledFor: string | null
}) {
  const overview = readDemoApprovalOverview(input.organization)
  const existing = overview.items.find((item) => item.contentItemId === input.contentItemId)

  if (!existing && input.contentStatus !== 'in_review') {
    return
  }

  if (!existing) {
    const approvalId = `approval-${input.contentItemId}`
    write(input.organization, [
      {
        id: approvalId,
        contentItemId: input.contentItemId,
        title: input.title,
        channel: input.channel,
        contentType: input.contentType,
        status: 'pending',
        contentStatus: input.contentStatus,
        scheduledFor: input.scheduledFor,
        dueAt: nextDueAt(),
        comment: 'Neu aus dem Content-Plan zur Freigabe eingereicht.',
        requestedFromUserId: 'demo-approver-1',
        assigneeLabel: 'Julia Freigabe',
      },
      ...overview.items,
    ])
    appendEvent(
      input.organization,
      approvalId,
      null,
      'pending',
      'Neu aus dem Content-Plan zur Freigabe eingereicht.',
      'System',
    )
    return
  }

  const nextStatus =
    input.contentStatus === 'in_review'
      ? 'pending'
      : input.contentStatus === 'draft'
        ? 'changes_requested'
        : input.contentStatus === 'approved' || input.contentStatus === 'scheduled' || input.contentStatus === 'published'
          ? 'approved'
          : existing.status

  const items = overview.items.map((item) =>
    item.contentItemId === input.contentItemId
      ? {
          ...item,
          title: input.title,
          channel: input.channel,
          contentType: input.contentType,
          contentStatus: input.contentStatus,
          status: nextStatus,
          scheduledFor: input.scheduledFor,
          dueAt: input.contentStatus === 'in_review' ? item.dueAt ?? nextDueAt() : item.dueAt,
        }
      : item,
  )

  write(input.organization, items)

  if (nextStatus !== existing.status) {
    appendEvent(input.organization, existing.id, existing.status, nextStatus, existing.comment, 'System')
  }
}

export function updateDemoApprovalAssignee(
  organization: OrganizationSummary | null,
  approvalId: string,
  requestedFromUserId: string | null,
  assigneeLabel: string,
) {
  const overview = readDemoApprovalOverview(organization)
  const items = overview.items.map((item) =>
    item.id === approvalId
      ? {
          ...item,
          requestedFromUserId,
          assigneeLabel,
        }
      : item,
  )

  write(organization, items)
}

export function seedDemoApprovalEvents(organization: OrganizationSummary | null) {
  const events = readEvents(organization)
  if (events.length === 0) {
    const seeded = buildInitialEvents(readDemoApprovalOverview(organization).items)
    writeEvents(organization, seeded)
    return seeded
  }

  writeEvents(organization, events)
  return events
}